import React, { useState, useEffect } from "react";
import { Line, Bar, Pie } from "react-chartjs-2";
import { buildApiUrl } from '../config';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "./Dashboard.css";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Add this standardized pie chart options
const pieChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
    }
  },
  layout: {
    padding: {
      top: 50,
      bottom: 50,
      left: 50,
      right: 50
    }
  }
};

// Define a consistent chart wrapper style
const chartWrapperStyle = {
  width: '300px',
  height: '250px',
  margin: '0 auto'
};

const Dashboard = () => {
  // Time filter for line chart
  const [timeFilter, setTimeFilter] = useState("last7Days");
  const [chartData, setChartData] = useState(null);

  // Overall Summary items
  const [overallSummary, setOverallSummary] = useState({
    totalSales: "0.00",
    serviceSales: "0.00",
    stockSales: "0.00",
    pendingReceivables: "0.00",
    pendingPayables: "0.00",
    totalBalance: "0.00",
    totalExpenses: "0.00",
    expensesTotal: "0.00",
    stockExpensesTotal: "0.00"
  });

  // Daily Summary items
  const [dailySummary, setDailySummary] = useState({
    totalSalesToday: "0.00",
    creditSalesToday: "0.00",
    oilFilterToday: "0.00",
    totalExpensesToday: "0.00",
    cashReceivedToday: "0.00",
    totalBalanceToday: "0.00"
  });

  // Bar chart: revenue vs expenses
  const [revenueVsExpensesData, setRevenueVsExpensesData] = useState(null);

  // Pie chart: top companies
  const [topCompaniesChartData, setTopCompaniesChartData] = useState(null);

  // Pie chart: top services (dummy or real if you have an endpoint)
  const [topServicesChartData, setTopServicesChartData] = useState(null);

  // Pie chart: payment method (dummy or real if you have an endpoint)
  const [paymentMethodChartData, setPaymentMethodChartData] = useState(null);

  // VAT Till Date (Collected / Paid including expense VAT / Remaining)
  const [vatTillDate, setVatTillDate] = useState(null);

  // Company Revenue Section
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companyRevenue, setCompanyRevenue] = useState("0.00");

  // ----------------------------------------------------------------
  // 1) FETCH LINE CHART DATA
  // ----------------------------------------------------------------
  const fetchChartData = async (period) => {
    try {
      const response = await fetch(
        buildApiUrl(`api/financial/line-chart?period=${period}`)
      );
      const data = await response.json();

      const formattedData = {
        labels: data.labels || [],
        datasets: [
          {
            label: "Total Sales",
            data: data.totalSales || [],
            borderColor: "#36A2EB",
            backgroundColor: "rgba(54, 162, 235, 0.5)",
            fill: true,
            tension: 0.4,
          },
          {
            label: "Credit Sales",
            data: data.creditSales || [],
            borderColor: "#FF6384",
            backgroundColor: "rgba(255, 99, 132, 0.5)",
            fill: true,
            tension: 0.4,
          },
          {
            label: "Expenses",
            data: data.expenses || [],
            borderColor: "#FFCE56",
            backgroundColor: "rgba(255, 206, 86, 0.5)",
            fill: true,
            tension: 0.4,
          },
          {
            label: "Balance",
            data: data.balance || [],
            borderColor: "#4BC0C0",
            backgroundColor: "rgba(75, 192, 192, 0.5)",
            fill: true,
            tension: 0.4,
          },
        ],
      };

      setChartData(formattedData);
    } catch (error) {
      console.error("Error fetching chart data:", error);
      setChartData(null);
    }
  };

  // ----------------------------------------------------------------
  // 2) FETCH OVERALL SUMMARY
  // ----------------------------------------------------------------
  const fetchOverallSummary = async () => {
    try {
      const [balancesRes, totalSalesRes, totalExpensesRes] = await Promise.all([
        fetch(buildApiUrl("api/financial/current-balances")),
        fetch(buildApiUrl("api/total-sales")),
        fetch(buildApiUrl("api/total-expenses"))
      ]);

      const [balancesData, salesData, expensesData] = await Promise.all([
        balancesRes.json(),
        totalSalesRes.json(),
        totalExpensesRes.json()
      ]);

      console.log('💰 Overall Summary Data:', {
        balances: balancesData,
        sales: salesData,
        expenses: expensesData
      });

      if (salesData.success && expensesData.success) {
        const totalSales = parseFloat(salesData.totalSales || 0);
        const totalExpenses = parseFloat(expensesData.totalExpenses || 0);
        const totalBalance = totalSales - totalExpenses;
        
        setOverallSummary(prev => ({
          ...prev,
          totalSales: salesData.totalSales || 0,
          serviceSales: salesData.serviceSales || 0,
          stockSales: salesData.stockSales || 0,
          pendingReceivables: balancesData.receivables || 0,
          pendingPayables: balancesData.payables || 0,
          totalExpenses: expensesData.totalExpenses || 0,
          expensesTotal: expensesData.expensesTotal || 0,
          stockExpensesTotal: expensesData.stockExpensesTotal || 0,
          totalBalance: totalBalance.toFixed(2)
        }));
      }
    } catch (error) {
      console.error("Error fetching overall summary:", error);
    }
  };

  // ----------------------------------------------------------------
  // 3) FETCH DAILY SUMMARY
  // ----------------------------------------------------------------
  const fetchDailySummary = async () => {
    try {
      console.log('🔄 Starting to fetch daily summary...');
      
      const [
        todaySalesRes, 
        todayExpensesRes, 
        todayCreditSalesRes, 
        todayOilFilterRes,
        keyMetricsRes,
      ] = await Promise.all([
        fetch(buildApiUrl("api/total-sales/today")),
        fetch(buildApiUrl("api/total-expenses/today")),
        fetch(buildApiUrl("api/credit-sales/today")),
        fetch(buildApiUrl("api/oil-filter-earnings/today")),
        fetch(buildApiUrl("api/financial/key-metrics")),
        fetch(buildApiUrl("api/dashboard/cash-received-today"))
      ]);

      console.log('✅ All API calls completed');

      const [
        salesData, 
        expensesData, 
        creditSalesData, 
        oilFilterData,
        metricsData
      ] = await Promise.all([
        todaySalesRes.json(),
        todayExpensesRes.json(),
        todayCreditSalesRes.json(),
        todayOilFilterRes.json(),
        keyMetricsRes.json()
      ]);

      console.log('📊 Today\'s Sales Data:', salesData);
      console.log('💸 Today\'s Expenses Data:', expensesData);
      console.log('💳 Today\'s Credit Sales Data:', creditSalesData);
      console.log('🛢️ Today\'s Oil+Filter Data:', oilFilterData);

      // Safely parse numeric values with fallbacks to 0
      const totalSales = parseFloat(salesData.totalSales || 0);
      const totalExpenses = parseFloat(expensesData.totalExpenses || 0);
      const totalCreditSales = parseFloat(creditSalesData?.totalCreditSales || 0);
      const totalOilFilterEarnings = parseFloat(oilFilterData?.totalOilFilterEarnings || 0);
      const totalBalance = totalSales - totalExpenses;

      console.log('💰 Parsed values:', {
        totalSales,
        totalExpenses,
        totalCreditSales,
        totalOilFilterEarnings,
        totalBalance
      });

      setDailySummary({
        totalSalesToday: totalSales.toFixed(2),
        serviceSalesToday: parseFloat(salesData.serviceSales || 0).toFixed(2),
        stockSalesToday: parseFloat(salesData.stockSales || 0).toFixed(2),
        creditSalesToday: totalCreditSales.toFixed(2),
        oilFilterToday: totalOilFilterEarnings.toFixed(2),
        totalExpensesToday: totalExpenses.toFixed(2),
        expensesTotalToday: parseFloat(expensesData.expensesTotal || 0).toFixed(2),
        stockExpensesTotalToday: parseFloat(expensesData.stockExpensesTotal || 0).toFixed(2),
        cashReceivedToday: parseFloat(metricsData.cashReceived || 0).toFixed(2),
        totalBalanceToday: totalBalance.toFixed(2)
      });
    } catch (error) {
      console.error("❌ Error fetching daily summary:", error);
    }
  };

  // ----------------------------------------------------------------
  // 4) FETCH REVENUE VS EXPENSES CHART
  // ----------------------------------------------------------------
  const fetchRevenueVsExpensesData = async (period) => {
    try {
      const res = await fetch(
        buildApiUrl(`api/financial/revenue-expenses?period=${period}`)
      );
      const data = await res.json();
      setRevenueVsExpensesData({
        labels: ["Revenue", "Expenses"],
        datasets: [
          {
            label: "Revenue vs Expenses",
            data: [data.revenue || 0, data.expenses || 0],
            backgroundColor: ["#36A2EB", "#FF6384"],
          },
        ],
      });
    } catch (error) {
      console.error("Error fetching revenue vs expenses:", error);
      setRevenueVsExpensesData(null);
    }
  };

  // ----------------------------------------------------------------
  // 5) FETCH TOP COMPANIES
  // ----------------------------------------------------------------
  const fetchTopCompanies = async (period) => {
    try {
      const url = period 
        ? buildApiUrl(`api/financial/top-companies?period=${period}`)
        : buildApiUrl("api/financial/top-companies");
      const res = await fetch(url);
      const data = await res.json();
      const labels = data.map((c) => c.company_name);
      const values = data.map((c) => c.total_revenue);

      setTopCompaniesChartData({
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF"],
          },
        ],
      });
    } catch (error) {
      console.error("Error fetching top companies:", error);
      setTopCompaniesChartData(null);
    }
  };

  // ----------------------------------------------------------------
  // 6) STILL-DUMMY CHARTS
  // ----------------------------------------------------------------
  const fetchTopServices = async (period) => {
    try {
      console.log("Fetching top services data...");
      const url = period
        ? buildApiUrl(`api/dashboard/top-services?period=${period}`)
        : buildApiUrl("api/dashboard/top-services");
      const res = await fetch(url);
      console.log("Top services response status:", res.status);
      
      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
      
      const response = await res.json();
      console.log("Top services data:", response);
      
      if (response.success && response.data && response.data.length > 0) {
        const labels = response.data.map(item => item.service_name);
        const values = response.data.map(item => item.total_amount);
        
        setTopServicesChartData({
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF"],
            },
          ],
        });
      } else {
        console.log("No top services data available");
        setTopServicesChartData({
          labels: ["No Data Available"],
          datasets: [
            {
              data: [1],
              backgroundColor: ["#e0e0e0"],
            },
          ],
        });
      }
    } catch (error) {
      console.error("Error fetching top services:", error);
      setTopServicesChartData({
        labels: ["Error Loading Data"],
        datasets: [
          {
            data: [1],
            backgroundColor: ["#e0e0e0"],
          },
        ],
      });
    }
  };

  const fetchPaymentMethods = async (period) => {
    try {
      console.log("Fetching payment methods data...");
      const url = period
        ? buildApiUrl(`api/dashboard/payment-methods?period=${period}`)
        : buildApiUrl("api/dashboard/payment-methods");
      const res = await fetch(url);
      console.log("Payment methods response status:", res.status);
      
      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
      
      const response = await res.json();
      console.log("Payment methods data:", response);
      
      if (response.success && response.data && response.data.length > 0) {
        const labels = response.data.map(item => item.payment_method || 'Unknown');
        const values = response.data.map(item => item.total_amount);
        
        setPaymentMethodChartData({
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: ["#36A2EB", "#FF6384", "#FFCE56", "#4BC0C0", "#9966FF"],
            },
          ],
        });
      } else {
        console.log("No payment methods data available");
        setPaymentMethodChartData({
          labels: ["No Data Available"],
          datasets: [
            {
              data: [1],
              backgroundColor: ["#e0e0e0"],
            },
          ],
        });
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      setPaymentMethodChartData({
        labels: ["Error Loading Data"],
        datasets: [
          {
            data: [1],
            backgroundColor: ["#e0e0e0"],
          },
        ],
      });
    }
  };

  // ----------------------------------------------------------------
  // 7) FETCH COMPANIES AND COMPANY REVENUE
  // ----------------------------------------------------------------
  const fetchCompanies = async () => {
    try {
      console.log("🏢 Fetching companies...");
      const res = await fetch(buildApiUrl("api/companies"));
      const data = await res.json();
      console.log("🏢 Companies API response:", data);
      
      // The existing API returns array directly, not wrapped in success object
      if (Array.isArray(data)) {
        // Convert the existing format {id, name} to {company_id, company_name}
        const formattedCompanies = data.map(company => ({
          company_id: company.id,
          company_name: company.name
        }));
        console.log("🏢 Formatted companies:", formattedCompanies);
        setCompanies(formattedCompanies);
      } else {
        console.log("🏢 API response is not an array:", typeof data);
        setCompanies([]);
      }
    } catch (error) {
      console.error("❌ Error fetching companies:", error);
      setCompanies([]);
    }
  };

  const fetchCompanyRevenue = async (companyId) => {
    if (!companyId) {
      setCompanyRevenue("0.00");
      return;
    }
    
    try {
      const res = await fetch(buildApiUrl(`api/dashboard/company-revenue/${companyId}`));
      const data = await res.json();
      if (data.success) {
        setCompanyRevenue(data.totalRevenue || "0.00");
      } else {
        setCompanyRevenue("0.00");
      }
    } catch (error) {
      console.error("Error fetching company revenue:", error);
      setCompanyRevenue("0.00");
    }
  };

  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    setSelectedCompany(companyId);
    fetchCompanyRevenue(companyId);
  };

  // ----------------------------------------------------------------
  // useEffect
  // ----------------------------------------------------------------
  const fetchVatTillDate = async () => {
    try {
      const res = await fetch(buildApiUrl("api/vat/overall-summary"));
      const data = await res.json();
      if (data.success) setVatTillDate(data.data);
      else setVatTillDate(null);
    } catch (e) {
      console.error("Error fetching VAT till date:", e);
      setVatTillDate(null);
    }
  };

  useEffect(() => {
    fetchChartData(timeFilter);
    fetchOverallSummary();
    fetchDailySummary();
    fetchRevenueVsExpensesData(timeFilter);
    fetchTopCompanies(timeFilter);
    fetchTopServices(timeFilter);
    fetchPaymentMethods(timeFilter);
    fetchCompanies(); // Fetch companies for dropdown
    fetchVatTillDate();
  }, [timeFilter]);

  // ----------------------------------------------------------------
  // RENDER
  // ----------------------------------------------------------------
  return (
    <div className="dashboard-container">
      <h1>Dashboard</h1>

      {/* Filter Dropdown */}
      <div className="filter-container">
        <label htmlFor="time-filter">Time Filter:</label>
        <select
          id="time-filter"
          value={timeFilter}
          onChange={(e) => setTimeFilter(e.target.value)}
        >
          <option value="today">Today</option>
          <option value="last7Days">Last 7 Days</option>
          <option value="lastMonth">Last Month</option>
          <option value="last3Months">Last 3 Months</option>
          <option value="lastYear">Last Year</option>
        </select>
      </div>

      {/* Line Chart */}
      <div className="chart-container line-chart" style={{ height: "400px" }}>
        {chartData ? (
          <Line
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: "bottom",
                  labels: {
                    padding: 20,
                    boxWidth: 15
                  }
                },
                title: {
                  display: true,
                  text: "Daily Sales & Expenses",
                },
              },
              scales: {
                x: {
                  ticks: {
                    callback: function(value, index, values) {
                      const dateStr = this.getLabelForValue(value);
                      if (!dateStr) return '';
                      
                      const date = new Date(dateStr);
                      if (isNaN(date)) return '';
                      
                      return date.getDate() + ' ' + date.toLocaleString('default', { month: 'short' });
                    },
                    maxRotation: 45,
                    minRotation: 45,
                    font: {
                      size: 10
                    },
                    autoSkip: true,
                    maxTicksLimit: 15
                  },
                  grid: {
                    display: true
                  }
                },
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Amount (AED)'
                  }
                }
              },
              layout: {
                padding: {
                  top: 30,
                  bottom: 50,
                  left: 30,
                  right: 30
                }
              }
            }}
          />
        ) : (
          <p>Loading chart data...</p>
        )}
      </div>

      {/* VAT Till Date */}
      <h2 className="summary-heading">VAT Till Date</h2>
      <div className="overall-summary">
        <div className="summary-item">
          <h3>Total VAT Collected</h3>
          <p>AED {Number(vatTillDate?.collectedVat || 0).toFixed(2)}</p>
        </div>
        <div className="summary-item">
          <h3>Paid</h3>
          <p>
            AED{" "}
            {Number(
              (parseFloat(vatTillDate?.expenseVat) || 0) +
                (parseFloat(vatTillDate?.alreadyPaid) || 0)
            ).toFixed(2)}
          </p>
          {/* Paid = Expense VAT + VAT payments already recorded to Govt */}
        </div>
        <div className="summary-item">
          <h3>Remaining</h3>
          <p>AED {Number(vatTillDate?.remaining || 0).toFixed(2)}</p>
        </div>
        {/* Kept for later — detailed split
        <div className="summary-item">
          <h3>Invoice VAT</h3>
          <p>AED {Number(vatTillDate?.invoiceVat || 0).toFixed(2)}</p>
        </div>
        <div className="summary-item">
          <h3>Thermal VAT</h3>
          <p>AED {Number(vatTillDate?.thermalVat || 0).toFixed(2)}</p>
        </div>
        <div className="summary-item">
          <h3>Expense VAT</h3>
          <p>AED {Number(vatTillDate?.expenseVat || 0).toFixed(2)}</p>
        </div>
        <div className="summary-item">
          <h3>Already Paid (Govt)</h3>
          <p>AED {Number(vatTillDate?.alreadyPaid || 0).toFixed(2)}</p>
        </div>
        */}
      </div>

      {/* Overall Summary */}
      <h2 className="summary-heading">Overall Summary</h2>
      <div className="overall-summary">
        <div className="summary-item">
          <h3>Total Balance</h3>
          <p>AED {Number(overallSummary.totalBalance).toFixed(2)}</p>
        </div>
        <div className="summary-item">
          <h3>Total Sales</h3>
          <p>AED {Number(overallSummary.totalSales).toFixed(2)}</p>
          {/* <small>Services: AED {Number(overallSummary.serviceSales).toFixed(2)}</small>
          <small>Stock: AED {Number(overallSummary.stockSales).toFixed(2)}</small> */}
        </div>
        <div className="summary-item">
          <h3>Total Expenses</h3>
          <p>AED {Number(overallSummary.totalExpenses).toFixed(2)}</p>
          {/* <small>Regular: AED {Number(overallSummary.expensesTotal).toFixed(2)}</small>
          <small>Stock: AED {Number(overallSummary.stockExpensesTotal).toFixed(2)}</small> */}
        </div>
        <div className="summary-item">
          <h3>Pending Receivables</h3>
          <p>AED {Number(overallSummary.pendingReceivables).toFixed(2)}</p>
        </div>
        <div className="summary-item">
          <h3>Pending Payables</h3>
          <p>AED {Number(overallSummary.pendingPayables).toFixed(2)}</p>
        </div>
      </div>

      {/* Daily Summary */}
      <h2 className="summary-heading">Today's Summary</h2>
      <div className="daily-summary">
        <div className="summary-item">
          <h3>Total Balance</h3>
          <p>AED {Number(dailySummary.totalBalanceToday).toFixed(2)}</p>
        </div>
        <div className="summary-item">
          <h3>Total Sales</h3>
          <p>AED {Number(dailySummary.totalSalesToday).toFixed(2)}</p>
          {/* <small>Services: AED {Number(dailySummary.serviceSalesToday).toFixed(2)}</small>
          <small>Stock: AED {Number(dailySummary.stockSalesToday).toFixed(2)}</small> */}
        </div>
        <div className="summary-item">
          <h3>Credit Sales</h3>
          <p>AED {dailySummary.creditSalesToday || "0.00"}</p>
        </div>
        <div className="summary-item">
          <h3>Oil+Filter Earnings</h3>
          <p>AED {dailySummary.oilFilterToday || "0.00"}</p>
        </div>
        <div className="summary-item">
          <h3>Total Expenses</h3>
          <p>AED {Number(dailySummary.totalExpensesToday).toFixed(2)}</p>
          {/* <small>Regular: AED {Number(dailySummary.expensesTotalToday).toFixed(2)}</small>
          <small>Stock: AED {Number(dailySummary.stockExpensesTotalToday).toFixed(2)}</small> */}
        </div>
        <div className="summary-item">
          <h3>Cash Received</h3>
          <p>AED {Number(dailySummary.cashReceivedToday).toFixed(2)}</p>
        </div>
      </div>

      {/* Company Revenue Section */}
      <h2 className="summary-heading">Company Revenue</h2>
      <div className="company-revenue-section">
        <div className="company-selector">
          <label htmlFor="company-select">Select Company:</label>
          <select
            id="company-select"
            value={selectedCompany}
            onChange={handleCompanyChange}
            style={{
              padding: "8px 12px",
              marginLeft: "10px",
              borderRadius: "4px",
              border: "1px solid #ddd",
              fontSize: "14px",
              minWidth: "200px"
            }}
          >
            <option value="">-- Select a Company --</option>
            {companies.map((company) => (
              <option key={company.company_id} value={company.company_id}>
                {company.company_name}
              </option>
            ))}
          </select>
        </div>
        <div className="company-revenue-display">
          <div className="summary-item" style={{ marginTop: "20px", textAlign: "center" }}>
            <h3>Total Revenue (Till Date)</h3>
            <p style={{ fontSize: "24px", fontWeight: "bold", color: "#36A2EB" }}>
              AED {Number(companyRevenue).toFixed(2)}
            </p>
            {selectedCompany && (
              <small style={{ color: "#666" }}>
                {companies.find(c => c.company_id == selectedCompany)?.company_name || "Selected Company"}
              </small>
            )}
          </div>
        </div>
      </div>

      {/* Insights Title */}
      <h2 className="summary-heading">Insights</h2>

      <div className="charts-section">
        {/* Revenue vs Expenses Bar */}
        <div className="chart-container revenue-vs-expenses">
          <h3 className="chart-label">Revenue vs Expenses</h3>
          {revenueVsExpensesData ? (
            <Bar
            data={revenueVsExpensesData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { position: "bottom" } },
              scales: {
                y: {
                  beginAtZero: true,
                },
              },
              layout: {
                padding: {
                  top: 30,
                  bottom: 30,
                  left: 30,
                  right: 30
                }
              }
            }}
          />
          ) : (
            <p>Loading...</p>
          )}
        </div>

        {/* Top Companies Pie */}
        <div className="chart-container top-companies-by-revenue">
          <h3 className="chart-label">Top Companies by Revenue</h3>
          {topCompaniesChartData ? (
            <Pie
              data={topCompaniesChartData}
              options={pieChartOptions}
            />
          ) : (
            <p>Loading...</p>
          )}
        </div>
      </div>

      <div className="charts-section">
        {/* Top Services/Products Chart */}
        <div className="chart-container top-services">
          <h3 className="chart-label">Top Services/Products</h3>
          {topServicesChartData ? (
            // <div style={chartWrapperStyle}>
              <Pie
                data={topServicesChartData}
                options={pieChartOptions}
              />
            // </div>
          ) : (
            <p>Loading...</p>
          )}
        </div>

        {/* Sales by Payment Method Chart */}
        <div className="chart-container sales-by-payment-method">
          <h3 className="chart-label">Sales by Payment Method</h3>
          {paymentMethodChartData ? (
            // <div style={chartWrapperStyle}>
              <Pie
                data={paymentMethodChartData}
                options={pieChartOptions}
              />
            // </div>
          ) : (
            <p>Loading...</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
